import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { Lotus, Feather } from "./Ornaments";
import { scrollToId } from "./Reveal";
import { whatsappLink, SHLOKA } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];
const LINES = [
  { text: "Every Occasion.", cls: "" },
  { text: "Extraordinary", cls: "italic text-terra" },
  { text: "Experiences.", cls: "" },
];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay: 0.2 + i * 0.14, ease }}>
      {children}
    </motion.span>
  </span>
);

const Petals = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    {[["8%", "18%", 0], ["88%", "12%", 2], ["70%", "78%", 4], ["18%", "82%", 1.5], ["48%", "8%", 3]].map(([l, t, d], i) => (
      <span key={i} className="petal" style={{ left: l, top: t, animationDelay: `${d}s` }} />
    ))}
  </div>
);

// Mobile (< sm / 640px) detection — used to disable the in-frame parallax/scale
// that shifts the painting inside the overflow-hidden arch and crops its bottom.
const MOBILE_QUERY = "(max-width: 639px)";
const useIsMobile = () => {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  return m;
};

export const Hero = () => {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative bg-blush jaali overflow-hidden pt-28 sm:pt-32 pb-12 sm:pb-20 lg:pb-28">
      <Petals />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.15 }}
        className="text-center px-5 pt-2"
        data-testid="hero-shloka"
      >
        <p className="font-sanskrit text-terra-deep text-base sm:text-lg leading-relaxed">
          {SHLOKA[0]}<br className="sm:hidden" /> <span className="hidden sm:inline"> </span>{SHLOKA[1]}
        </p>
      </motion.div>
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-5 sm:gap-12 lg:gap-8 items-center pt-10">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }} className="inline-flex items-center gap-2 rounded-full bg-cream/80 border border-gold/40 px-4 py-2 text-xs tracking-[0.22em] uppercase text-terra-deep font-semibold">
            <Lotus className="w-5 h-4" /> Indian celebrations, planned with love
          </motion.p>
          <h1 data-testid="hero-heading" className="font-display text-ink mt-7 text-[3.1rem] leading-[0.98] sm:text-7xl lg:text-[6.4rem] tracking-[-0.02em]">
            {LINES.map((l, i) => <MaskLine key={l.text} i={i} className={l.cls}>{l.text}</MaskLine>)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.75, ease }} className="mt-8 max-w-xl text-base md:text-lg text-muted-ink leading-relaxed">
            Nexora Events plans weddings, destination shaadis, engagements, birthdays, corporate galas and Bhagwat Katha gatherings — with trusted vendors and packages shaped around your family.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.9, ease }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-plan-button" onClick={() => scrollToId("enquiry")} className="btn-pink text-base px-7 py-4">
              Plan my celebration <ArrowDownRight size={18} className="transition-transform group-hover:rotate-[-45deg]" />
            </button>
            <a data-testid="hero-whatsapp-button" href={whatsappLink()} target="_blank" rel="noreferrer" className="btn-ghost text-base px-7 py-4">
              <MessageCircle size={18} /> Chat on WhatsApp
            </a>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 1 }} className="mt-8 sm:mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-muted-ink">
            {["Verified vendor network", "Transparent packages", "One dedicated planner"].map((t) => (
              <li key={t} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-sage" />{t}</li>
            ))}
          </motion.ul>
        </div>

        <div className="lg:col-span-5 relative" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={reset}>
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 32px 32px)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)" }}
            transition={{ duration: 1.4, delay: 0.3, ease }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="floaty relative -mx-5 sm:mx-auto w-auto sm:w-[70%] lg:w-full max-w-none sm:max-w-[560px] aspect-[736/1307] sm:aspect-[4/5.2] rounded-t-full rounded-b-[32px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(201,107,82,0.45)] border-[6px] border-cream bg-cream"
            data-testid="hero-arch-image"
          >
            <motion.img style={isMobile ? { y: 0, scale: 1, objectPosition: "50% 50%" } : { y: imgY, scale: 1.05, objectPosition: "50% 30%" }} src="/images/krishna.jpg" alt="Radha and Krishna — Lord Krishna playing the flute" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div style={{ y: isMobile ? 0 : cardY }} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 1.3, ease }} className="absolute left-3 top-[28%] sm:left-auto sm:right-6 sm:top-10 lg:-right-4 bg-cream rounded-2xl px-4 py-3 shadow-lg border border-gold/30 flex items-center gap-3 z-10">
            <Feather className="w-4 h-10 sway" />
            <div>
              <p className="font-display text-lg leading-none text-ink">ॐ शुभ आरम्भ</p>
              <p className="text-xs text-muted-ink mt-1">Every auspicious beginning</p>
            </div>
          </motion.div>
          <div className="absolute bottom-5 right-1 sm:-bottom-10 sm:right-8 z-10 bg-cream rounded-full spin-slow" aria-hidden="true">
            <svg viewBox="0 0 120 120" className="w-24 h-24">
              <defs><path id="circ" d="M60 60 m-46 0 a46 46 0 1 1 92 0 a46 46 0 1 1 -92 0" /></defs>
              <text className="fill-terra-deep" style={{ fontSize: 11, letterSpacing: 3.2 }}><textPath href="#circ">NEXORA EVENTS · EST. WITH LOVE · </textPath></text>
            </svg>
            <Lotus className="w-8 h-6 absolute inset-0 m-auto" />
          </div>
        </div>
      </div>
    </section>
  );
};
